import React, { useState, useRef, useEffect } from 'react';
import { advisorService } from '../../services/advisor.service';
import { Logo, SendIcon, SparkleIcon, ArrowRightIcon } from '../UI/Icons';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const suggestedQuestions = [
  'What courses should I take next term?',
  'How am I doing compared to last term?',
  'What are the prerequisites for CS 341?',
  'Which courses fit my remaining requirements?',
];

const AdvisorChat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content:
        "Hi! I'm Pathfinder. I can see your transcript, your planned terms, and the live UW course catalog — ask me anything about what to take next.",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (question: string) => {
    if (!question.trim() || loading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: question.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await advisorService.queryAdvisor({ question: question.trim() });
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.response,
          timestamp: new Date(),
        },
      ]);
    } catch (error) {
      console.error('Failed to get advisor response:', error);
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: "I'm sorry, I ran into an error reaching the advisor. Please try again.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send(input);
  };

  return (
    <div className="flex h-full flex-col p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
          <SparkleIcon width={21} height={21} />
        </span>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-canvas-900">
            AI Advisor
          </h1>
          <p className="text-sm text-canvas-600">
            Grounded in your transcript, schedule, and the live catalog
          </p>
        </div>
      </div>

      {/* Conversation */}
      <div className="card mt-5 flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="custom-scrollbar flex-1 space-y-5 overflow-y-auto p-5">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
            >
              {message.role === 'assistant' ? (
                <Logo size={32} className="shrink-0" />
              ) : (
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-canvas-100 text-xs font-semibold text-canvas-700">
                  You
                </span>
              )}
              <div className={`max-w-[75%] ${message.role === 'user' ? 'text-right' : ''}`}>
                <div
                  className={`inline-block rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    message.role === 'user'
                      ? 'bg-ink text-white'
                      : 'bg-canvas-100 text-canvas-800'
                  }`}
                >
                  <p className="whitespace-pre-wrap text-left">{message.content}</p>
                </div>
                <p className="mt-1.5 px-1 text-[11px] text-canvas-500">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex gap-3">
              <Logo size={32} className="shrink-0" />
              <div className="rounded-2xl bg-canvas-100 px-4 py-4">
                <div className="flex gap-1.5">
                  {[0, 0.15, 0.3].map((delay) => (
                    <span
                      key={delay}
                      className="h-1.5 w-1.5 animate-bounce rounded-full bg-canvas-400"
                      style={{ animationDelay: `${delay}s` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions */}
        {messages.length === 1 && (
          <div className="border-t border-canvas-200 px-5 py-4">
            <p className="text-xs font-medium uppercase tracking-wide text-canvas-500">
              Try asking
            </p>
            <div className="mt-2.5 grid gap-2 sm:grid-cols-2">
              {suggestedQuestions.map((question) => (
                <button
                  key={question}
                  onClick={() => send(question)}
                  className="group flex items-center justify-between gap-2 rounded-xl border border-canvas-200 px-3.5 py-2.5 text-left text-sm text-canvas-700 transition-all hover:-translate-y-0.5 hover:border-primary-200 hover:text-canvas-900 hover:shadow-card"
                >
                  {question}
                  <ArrowRightIcon
                    width={15}
                    height={15}
                    className="shrink-0 text-canvas-400 transition-transform group-hover:translate-x-0.5"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Composer */}
        <form onSubmit={handleSubmit} className="flex gap-2 border-t border-canvas-200 p-4">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about courses, prerequisites, or your plan…"
            disabled={loading}
            className="input-field flex-1 rounded-full px-5"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="btn-primary aspect-square w-11 !px-0"
            aria-label="Send message"
          >
            {loading ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              <SendIcon width={17} height={17} />
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdvisorChat;
